import React from 'react';
import { useTranslation } from 'react-i18next';

import { RichTextEditor, Spinner } from '../../../shared/components';
import { EmailAgentSignature, getMyEmailSignature, saveMyEmailSignature } from '../services/emailComposeService';
import detailsStyles from './ConversationDetails.module.css';
import styles from './EmailComposeModal.module.css';

interface EmailSignatureEditorProps {
  onSaved: (signature: EmailAgentSignature) => void;
  onCancel: () => void;
}

/** Edits the signed-in agent's personal email signature. */
const EmailSignatureEditor: React.FC<EmailSignatureEditorProps> = ({ onSaved, onCancel }) => {
  const { t } = useTranslation(['conversations', 'common']);
  const [signature, setSignature] = React.useState<EmailAgentSignature | null>(null);
  const [draft, setDraft] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [insertRequest, setInsertRequest] = React.useState<{ id: number; html: string } | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    getMyEmailSignature()
      .then(result => {
        if (!cancelled) {
          setSignature(result);
          setDraft(result.bodyHtml);
        }
      })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : t('emailSignatureLoadFailed')); });
    return () => { cancelled = true; };
  }, [t]);

  const save = async (bodyHtml: string) => {
    setSaving(true);
    setError(null);
    try {
      onSaved(await saveMyEmailSignature(bodyHtml));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('emailSignatureSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (!signature) {
    return error ? <div className={detailsStyles.modalError}>{error}</div> : <div className={styles.loading}><Spinner /></div>;
  }

  return (
    <div className={styles.signatureEditor} role="group" aria-label={t('emailSignatureTitle')}>
      <div className={styles.signatureHead}>
        <span>{t('emailSignatureTitle')}</span>
        <span className={styles.hint}>{t('emailSignatureHint')}</span>
      </div>
      <RichTextEditor
        value={draft}
        onChange={setDraft}
        minHeight={90}
        ariaLabel={t('emailSignatureBody')}
        insertRequest={insertRequest}
      />
      <div className={styles.variableRow}>
        <span className={styles.hint}>{t('emailSignatureVariables')}</span>
        {signature.variables.map(variable => (
          <button
            key={variable}
            type="button"
            className={styles.variableChip}
            onClick={() => setInsertRequest(prev => ({ id: (prev?.id ?? 0) + 1, html: `{{${variable}}}` }))}
            disabled={saving}
          >
            {`{{${variable}}}`}
          </button>
        ))}
      </div>
      {error ? <div className={detailsStyles.modalError}>{error}</div> : null}
      <div className={styles.signatureActions}>
        {signature.bodyHtml ? (
          <button type="button" className={detailsStyles.modalSecondaryBtn} onClick={() => { void save(''); }} disabled={saving}>
            {t('emailSignatureRemove')}
          </button>
        ) : null}
        <span className={styles.spacer} />
        <button type="button" className={detailsStyles.modalSecondaryBtn} onClick={onCancel} disabled={saving}>
          {t('cancel', { ns: 'common' })}
        </button>
        <button
          type="button"
          className={detailsStyles.modalPrimaryBtn}
          onClick={() => { void save(draft); }}
          disabled={saving || draft === signature.bodyHtml}
        >
          {saving ? t('emailSignatureSaving') : t('emailSignatureSave')}
        </button>
      </div>
    </div>
  );
};

export default EmailSignatureEditor;
